import { useCallback, useState } from 'react';
import { isWithinLimit } from '@inference.engine/file-utils/web';
import { useFeatureAgent } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { peekChatStoreBindings } from '@/features/chats/lib/chat-store-bindings';
import {
  useChatActions,
  useChatStoreInput,
  useCurrentAgent,
  useCurrentAgentId,
  useIsAiBusy,
  useIsConnected,
  useIsCurrentAgentBusy,
} from '@/features/chats/stores';
import { notificationService } from '@/lib/services/notification.service';
import { FILE_VALIDATION_MESSAGES } from '@/shared/constants/file-upload.constants';
import { i18n } from '@/shared/i18n';
import { FileAssetWithUrl } from '../../model/chats.types';
import { persistTemplatesSettingsFromStore } from '../useChatSettingsPersistence';
import { useFileAttachments } from '../useFileAttachments';
import { FileStatus } from '../useFileAttachments/useFileAttachments.types';
import type { ChatInputModelOptions, UseChatInputProps } from './useChatInput.types';

export const useChatInput = ({ isNewChat, onSend }: UseChatInputProps) => {
  const agent = useCurrentAgent();
  const currentAgentId = useCurrentAgentId();
  const isConnected = useIsConnected() || !!isNewChat;

  // AI status selectors
  const isAiBusy = useIsAiBusy();
  const isCurrentAgentBusy = useIsCurrentAgentBusy();
  const { setUiStatus } = useChatActions();

  // Derived: another agent is busy during handoff (not the current one)
  const isOtherAgentBusy = isAiBusy && !isCurrentAgentBusy;

  const { chatInput, setValue, setOptions } = useChatStoreInput();
  // The caller resolves workspaceId and passes it to the entity hook.
  const workspaceId = useCurrentWorkspaceId();
  const { agent: handoffAgent, isLoading } = useFeatureAgent(currentAgentId, workspaceId);
  const displayAgent = handoffAgent ?? agent;

  const getModelFromContext = useCallback(() => {
    const input = chatInput.options as ChatInputModelOptions | undefined;
    if (input?.llmOptions?.model) {
      return input.llmOptions.model;
    }

    return undefined;
  }, [chatInput.options]);

  // Use the file attachments hook
  const { files, isUploading, handleInputFileUpload, handleFileUpload, handleRemoveFile } =
    useFileAttachments({
      getModelFromContext,
    });

  const isWaiting = isAiBusy || !isConnected || !displayAgent?.id || isLoading;

  const validFiles = useCallback(
    (fileInputs: FileAssetWithUrl[]): boolean => {
      if (fileInputs.length > 0) {
        // Determine which model will be used
        const agentModel = getModelFromContext();

        // Validate total file size
        const validation = isWithinLimit(fileInputs, agentModel);

        if (!validation.isValid) {
          notificationService.show(
            FILE_VALIDATION_MESSAGES.SIZE_EXCEEDED(
              i18n.t('common:constants.fileUpload.files'),
              Math.floor(validation.maxAllowed / (1024 * 1024)),
            ),
          );
          return false;
        }
      }

      return true;
    },
    [getModelFromContext],
  );

  /** Whether a send attempt would do anything at all — there is content, and nothing is in flight. */
  const hasSomethingToSend = useCallback(() => {
    const hasReadyFiles = chatInput.files.some(
      (file) => file.status === FileStatus.UPLOADED || file.status === FileStatus.REFERENCED,
    );
    return Boolean(chatInput.value.trim() || hasReadyFiles) && !isAiBusy && !isUploading;
  }, [chatInput.files, chatInput.value, isAiBusy, isUploading]);

  const performSend = useCallback(() => {
    if (hasSomethingToSend()) {
      // Convert FileInputWithStatus to FileInput for the API
      const fileInputs: FileAssetWithUrl[] = chatInput.files
        .filter((file) => ['uploaded', 'referenced'].includes(file.status)) // Only send uploaded files
        .map(({ assetId, file, url, metadata }) => ({
          assetId,
          url,
          mimeType: file.type,
          metadata,
        }));

      if (validFiles(fileInputs)) {
        // Set UI status for instant feedback - SDK will clear this when it responds
        setUiStatus('submitting');

        // Extract knowledgeBaseIds from options and pass separately
        const { knowledgeBaseIds, ...otherInputs } = chatInput.options || {};
        void onSend({
          agentId: currentAgentId,
          prompt: chatInput.value,
          inputs: otherInputs,
          knowledgeBaseIds: knowledgeBaseIds as string[],
          files: fileInputs,
        });
        // Snapshot the settings into the session entry on send (creates/refreshes `session:<chatId>`
        // once the session exists). No-op for non-templates. New-chat first send is captured in
        // StreamChatSession, since here the session id may not exist yet.
        persistTemplatesSettingsFromStore();
        setValue('');
        // Project context is a per-message attachment (unlike the
        // session-level knowledgeBaseIds): clear it after sending so it isn't
        // silently re-sent on the next message and its chips don't linger.
        setOptions((prev) => {
          if (!prev?.projectContext) return prev;
          const next = { ...prev };
          delete next.projectContext;
          return next;
        });
      }
    }
  }, [
    currentAgentId,
    hasSomethingToSend,
    onSend,
    setUiStatus,
    setValue,
    setOptions,
    chatInput.files,
    chatInput.options,
    chatInput.value,
    validFiles,
  ]);

  // The agent answers from the last SAVED widget artifact, so sending with unsaved manual edits in an
  // open widget silently discards them (and any lock set with them). Ask first. Re-checked on every
  // send rather than subscribed to, so the composer doesn't re-render on each keystroke in the editor.
  const [unsavedEditsPromptOpen, setUnsavedEditsPromptOpen] = useState(false);

  // The one piece of the content-lock work that is NOT scoped to the templates agent: `useArtifactSave`
  // runs for every consumer of the email widget, so this modal can appear for any of them and there is
  // no other way to switch it off. Flagged off by default until the rollout.
  const unsavedEditsWarningEnabled = useBooleanFlagValue(
    'unsaved_widget_edits_send_warning',
    false,
  );

  const handleSendMessageInternal = useCallback(() => {
    // Advisory only: neither an unbound store nor a partial one may block a send, hence the
    // non-throwing `peek` and the optional call. Gated on there being a real send to make, so an
    // Enter on an empty composer never raises a modal.
    const widgetStore = peekChatStoreBindings()?.widgetStore;
    if (
      unsavedEditsWarningEnabled &&
      hasSomethingToSend() &&
      widgetStore?.useWidgetStore.getState().hasUnsavedWidgetEdits?.()
    ) {
      setUnsavedEditsPromptOpen(true);
      return;
    }
    performSend();
  }, [unsavedEditsWarningEnabled, hasSomethingToSend, performSend]);

  // Confirming re-reads the live input rather than replaying a snapshot, so an edit made while the
  // modal was open is the thing that gets sent.
  const confirmSendWithUnsavedEdits = useCallback(() => {
    setUnsavedEditsPromptOpen(false);
    performSend();
  }, [performSend]);

  const cancelSendWithUnsavedEdits = useCallback(() => setUnsavedEditsPromptOpen(false), []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
    },
    [setValue],
  );

  return {
    currentAgentId,
    displayAgent,
    input: chatInput.value,
    files,
    isUploading,
    isConnected,
    isAiBusy,
    isWaiting,
    isCurrentAgentBusy,
    isOtherAgentBusy,
    options: chatInput.options,
    handleInputChange,
    handleInputFileUpload,
    handleSendMessageInternal,
    handleFileUpload,
    handleRemoveFile,
    updateOptions: setOptions,
    unsavedEditsPromptOpen,
    confirmSendWithUnsavedEdits,
    cancelSendWithUnsavedEdits,
  };
};
